/**
 * PriceDisplay — Consistent price formatting with currency support.
 * Handles INR formatting, discounts, and strikethrough prices.
 */

import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";

type PriceDisplayProps = {
  amount: number;
  /** Original price before discount (shows strikethrough) */
  compareAt?: number;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  className?: string;
};

/**
 * Format a number as currency using the site's configured currency.
 */
export function formatPrice(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: siteConfig.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function PriceDisplay({ amount, compareAt, size = "md", className }: PriceDisplayProps) {
  const sizeClasses = {
    sm: "text-sm",
    md: "text-lg font-display",
    lg: "text-3xl font-display",
  };

  const hasDiscount = compareAt && compareAt > amount;
  const discountPercent = hasDiscount ? Math.round(((compareAt - amount) / compareAt) * 100) : 0;

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className={cn("tabular-nums", sizeClasses[size])}>{formatPrice(amount)}</span>
      {hasDiscount && (
        <>
          <span className="tabular-nums text-sm text-muted-foreground line-through">
            {formatPrice(compareAt)}
          </span>
          <span className="text-xs font-medium text-accent">{discountPercent}% off</span>
        </>
      )}
    </span>
  );
}
